/**
 * Discount code state and handlers for checkout.
 * API integration separated from pure validation logic.
 */
import { useState, useCallback, useRef, useEffect } from 'react';

import { mapErrorCodeToMessage, DiscountErrorCode } from '../constants/discountErrorMapper';
import type { Cart, CartAction } from '../types/discount.types';
import { validateAppliedDiscount } from '../utils/discountValidation';

export interface UseCheckoutDiscountsOptions {
  /** cartUtil updateCart */
  updateCart: (action: CartAction) => Promise<Cart | null>;
  /** cartUtil getCart */
  getCart: () => Promise<Cart | null>;
  /** set cart state in the parent */
  setCart: (cart: Cart) => void;
  /** set discount error message (caller owns state) */
  setDiscountError: (error: string | null) => void;
  /** reset payment intent secret */
  setClientSecret?: ((secret: string | null) => void) | null;
  /** reset payment intent id */
  setPiId?: ((id: string | null) => void) | null;
  /** invalidate tax when discount changes */
  setTaxSummary?: ((summary: unknown) => void) | null;
  /** set generic page-level error */
  setError?: ((error: string | null) => void) | null;
}

export function useCheckoutDiscounts({
  updateCart,
  getCart,
  setCart,
  setDiscountError,
  setClientSecret,
  setPiId,
  setTaxSummary,
  setError,
}: UseCheckoutDiscountsOptions) {
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  /**
   * Stable ref relay for setDiscountError.
   *
   * setDiscountError is a useState setter from the parent — React guarantees
   * it is stable, but ESLint cannot prove that through a function parameter.
   * Storing it in a ref gives useCallback a stable reference while always
   * calling the latest version of the function.
   */
  const setDiscountErrorRef = useRef(setDiscountError);
  useEffect(() => { setDiscountErrorRef.current = setDiscountError; }, [setDiscountError]);

  const handleApplyDiscount = useCallback(
    async (code: string) => {
      if (!code) return;
      setApplyingDiscount(true);
      setDiscountErrorRef.current(null);
      if (setError) setError(null);
      if (setClientSecret) setClientSecret(null);
      if (setPiId) setPiId(null);
      if (setTaxSummary) setTaxSummary(null);

      try {
        const updated = await updateCart({
          action: 'addDiscountCode',
          code,
        });

        if (updated && Array.isArray(updated.discountCodes)) {
          // Re-fetch to guarantee expanded lineItems details for product specific discounts
          const latestCart = await getCart();
          const targetCart = latestCart ?? updated;

          const validation = validateAppliedDiscount(code, targetCart);

          if (!validation.isValid) {
            if (validation.requiresRemoval && validation.discountCodeId) {
              try {
                const cartAfterRemove = await updateCart({
                  action: 'removeDiscountCode',
                  discountCode: { typeId: 'discount-code', id: validation.discountCodeId },
                });
                if (cartAfterRemove) setCart(cartAfterRemove);
              } catch (removeErr) {
                // Ineligible discount removal failed — ensure cart state is consistent with a fresh fetch
                console.error('[useCheckoutDiscounts] Failed to remove ineligible discount:', removeErr);
                const freshCart = await getCart();
                if (freshCart) setCart(freshCart);
                throw removeErr; // Re-throw to be caught by outer catch if needed
              } finally {
                setApplyingDiscount(false); // Ensure loading state is cleared
              }
            } else {
              setCart(targetCart);
            }
            setDiscountErrorRef.current(mapErrorCodeToMessage(validation.errorCode));
          } else {
            setCart(targetCart);
            setDiscountErrorRef.current(null);
          }
        } else {
          setDiscountErrorRef.current(mapErrorCodeToMessage(DiscountErrorCode.UNABLE_TO_APPLY));
        }
      } catch (err) {
        console.error('[useCheckoutDiscounts] Failed to apply discount code:', err);
        setDiscountErrorRef.current(mapErrorCodeToMessage(DiscountErrorCode.INVALID_CODE));
        if (setError) setError(null);
      } finally {
        setApplyingDiscount(false);
      }
    },
    [
      updateCart,
      getCart,
      setCart,
      setClientSecret,
      setPiId,
      setTaxSummary,
      setError,
    ]
  );

  const handleRemoveDiscount = useCallback(
    async (discountId: string) => {
      try {
        if (setClientSecret) setClientSecret(null);
        if (setPiId) setPiId(null);
        if (setTaxSummary) setTaxSummary(null);

        const updated = await updateCart({
          action: 'removeDiscountCode',
          discountCode: { typeId: 'discount-code', id: discountId },
        });
        if (updated) {
          setCart(updated);
          setDiscountErrorRef.current(null);
          if (setError) setError(null);
        } else {
          const freshCart = await getCart();
          if (freshCart) setCart(freshCart);
        }
      } catch (err) {
        console.error('[useCheckoutDiscounts] Failed to remove discount code:', err);
        try {
          const freshCart = await getCart();
          if (freshCart) {
            setCart(freshCart);
            const stillHasDiscount = freshCart.discountCodes?.some(
              (dc) => dc.discountCode?.id === discountId
            );
            if (!stillHasDiscount) {
              setDiscountErrorRef.current(null);
              if (setError) setError(null);
            } else {
              if (setError) setError(mapErrorCodeToMessage(DiscountErrorCode.REMOVE_FAILED));
            }
          }
        } catch (reloadErr) {
          // Failed to reload cart after removal error — propagate removal failure message to UI
          console.error('[useCheckoutDiscounts] Failed to reload cart after removal error:', reloadErr);
          if (setError) setError(mapErrorCodeToMessage(DiscountErrorCode.REMOVE_FAILED));
        }
      }
    },
    [updateCart, getCart, setCart, setClientSecret, setPiId, setTaxSummary, setError]
  );

  return {
    applyingDiscount,
    handleApplyDiscount,
    handleRemoveDiscount,
  };
}
